import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { concatMap, map, catchError, of, withLatestFrom } from "rxjs";
import { DeliveryTermsClient } from "src/api-service";
import { selectSelectedCompany } from "../../../../user/user.selectors";
import { DeliveryTermsActions } from "./delivery-terms.actions";
import { DataActions } from "../../../../application/data/data.actions";

@Injectable()
export class DeliveryTermsEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly deliveryTermsClient = inject(DeliveryTermsClient);

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeliveryTermsActions.load),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([, selectedCompany]) =>
                this.deliveryTermsClient
                    .getAllAdmin(selectedCompany.id, 1, 1000)
                    .pipe(
                        map(response =>
                            DeliveryTermsActions.loadSuccess({ deliveryTerms: response.data ?? [] }),
                        ),
                        catchError(error =>
                            of(
                                DeliveryTermsActions.loadFailure({
                                    error,
                                    toastKey: "ADMIN_PANEL.EDIT_DELIVERY_TERM_SECTIONS.TOAST.LOAD_ERROR",
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    createDeliveryTerm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeliveryTermsActions.createDeliveryTerm),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ request }, selectedCompany]) =>
                this.deliveryTermsClient.create(request, selectedCompany.id).pipe(
                    concatMap(() => [
                        DeliveryTermsActions.createDeliveryTermSuccess({
                            toastKey: "ADMIN_PANEL.EDIT_DELIVERY_TERM_SECTIONS.TOAST.CREATE_SUCCESS",
                        }),
                        DeliveryTermsActions.load(),
                        DataActions.initializeData({ companyId: selectedCompany.id }),
                    ]),
                    catchError(error =>
                        of(
                            DeliveryTermsActions.createDeliveryTermFailure({
                                error,
                                toastKey: "ADMIN_PANEL.EDIT_DELIVERY_TERM_SECTIONS.TOAST.CREATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    updateDeliveryTerm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeliveryTermsActions.updateDeliveryTerm),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ request, id }, selectedCompany]) =>
                this.deliveryTermsClient.update(id, request, selectedCompany.id).pipe(
                    concatMap(() => [
                        DeliveryTermsActions.updateDeliveryTermSuccess({
                            toastKey: "ADMIN_PANEL.EDIT_DELIVERY_TERM_SECTIONS.TOAST.UPDATE_SUCCESS",
                        }),
                        DeliveryTermsActions.load(),
                        DataActions.initializeData({ companyId: selectedCompany.id }),
                    ]),
                    catchError(error =>
                        of(
                            DeliveryTermsActions.updateDeliveryTermFailure({
                                error,
                                toastKey: "ADMIN_PANEL.EDIT_DELIVERY_TERM_SECTIONS.TOAST.UPDATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    deleteDeliveryTerm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeliveryTermsActions.deleteDeliveryTerm),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            concatMap(([{ id }, selectedCompany]) =>
                this.deliveryTermsClient.delete(id, selectedCompany.id).pipe(
                    concatMap(() => [
                        DeliveryTermsActions.deleteDeliveryTermSuccess({
                            toastKey: "ADMIN_PANEL.EDIT_DELIVERY_TERM_SECTIONS.TOAST.DELETE_SUCCESS",
                        }),
                        DeliveryTermsActions.load(),
                        DataActions.initializeData({ companyId: selectedCompany.id }),
                    ]),
                    catchError(error =>
                        of(
                            DeliveryTermsActions.deleteDeliveryTermFailure({
                                error,
                                toastKey: "ADMIN_PANEL.EDIT_DELIVERY_TERM_SECTIONS.TOAST.DELETE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );
}
